import { Box, Typography, Button, Grid, Divider, Accordion, AccordionDetails, AccordionSummary } from '@mui/material'
import { FC, useCallback, useEffect, useState } from 'react'
import DraggableList from 'src/components/DraggableList'
import MasterWorkoutExerciseViewModel from 'src/viewmodels/MasterWorkoutExerciseViewModel'
import MasterWorkoutExerciseDaysItem from './MasterWorkoutExerciseDaysItem'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

interface MasterWorkoutExerciseDaysListProps {
	items: Map<string, MasterWorkoutExerciseViewModel[]>
	days: number[]
	onCreateMWE: (day: number, displayOrder: number) => void
	onEditMWE: (vm: MasterWorkoutExerciseViewModel) => void
	onOrderChange: (items: MasterWorkoutExerciseViewModel[]) => void
	canEdit: boolean
	enableRepsPerSet: boolean
	enableRirPerSet: boolean
	activeMasterWorkoutExercise?: MasterWorkoutExerciseViewModel
}

const MasterWorkoutExerciseDaysList: FC<MasterWorkoutExerciseDaysListProps> = ({
	items,
	days,
	onCreateMWE,
	onEditMWE,
	onOrderChange,
	canEdit,
	enableRepsPerSet,
	enableRirPerSet,
	activeMasterWorkoutExercise
}) => {
	const [selected, setSelected] = useState<{ week: number; day: number; displayOrder: number }>()

	// Track expanded state by day number instead of array index to persist user toggles across renders
	const [expanded, setExpanded] = useState<Record<number, boolean>>({})

	useEffect(() => {
		if (activeMasterWorkoutExercise?.masterWorkoutExercise) {
			const { week, day, displayOrder } = activeMasterWorkoutExercise.masterWorkoutExercise
			setSelected({ week, day, displayOrder })
		}
	}, [activeMasterWorkoutExercise])

	const handleSelect = useCallback((vm: MasterWorkoutExerciseViewModel) => {
		setSelected({ week: vm.masterWorkoutExercise.week, day: vm.masterWorkoutExercise.day, displayOrder: vm.masterWorkoutExercise.displayOrder })
		onEditMWE(vm)
	}, [])

	const handleOrderChange = useCallback((items: MasterWorkoutExerciseViewModel[]) => {
		setSelected(undefined)
		onOrderChange(items)
	}, [onOrderChange])

	useEffect(() => {
		// Only initialize days that are not yet tracked in the expanded state.
		// This prevents overwriting the user's manual collapse/expand actions when the days array reference changes.
		setExpanded((prevState) => {
			const newState = { ...prevState }
			let hasChanges = false

			days.forEach((day) => {
				if (newState[day] === undefined) {
					newState[day] = true
					hasChanges = true
				}
			})

			return hasChanges ? newState : prevState
		})
	}, [days])

	const handleAccordionChange = (day: number) => {
		setExpanded((prevState) => ({
			...prevState,
			[day]: !prevState[day]
		}))
	}

	return (
		<Grid container>
			{days.map((day) => {
				const values = items.get(`${day}`)
				return (
					<Grid key={day} item xs={12}>
						<Accordion key={day} expanded={expanded[day] || false}>
							<AccordionSummary
								expandIcon={
									<ExpandMoreIcon
										onClick={(e) => {
											e.stopPropagation()
											handleAccordionChange(day)
										}}
									/>
								}
							>
								<Box display="flex" flex="auto">
									<Typography variant="h4" sx={{ flexGrow: 1 }}>
										Day {day}
									</Typography>
								</Box>
								{canEdit && (
									<Button
										sx={{ mx: 1 }}
										variant="outlined"
										onClick={(e: any) => {
											e.stopPropagation()
											e.preventDefault()
											onCreateMWE(day, values?.length || 0)
										}}>
										Create
									</Button>
								)}
							</AccordionSummary>
							<AccordionDetails>
								{values?.length > 0 && (
									<DraggableList
										id={`day-${day}`}
										key={day}
										items={values}
										onOrderChange={handleOrderChange}
										disableDrag={!canEdit}
										renderItem={(vm: MasterWorkoutExerciseViewModel) => {
											const isSelected =
												selected &&
												selected.week === vm.masterWorkoutExercise.week &&
												selected.day === vm.masterWorkoutExercise.day &&
												selected.displayOrder === vm.masterWorkoutExercise.displayOrder
											return <MasterWorkoutExerciseDaysItem onSelect={handleSelect} canEdit={canEdit} viewModel={vm} selected={isSelected} enableRepsPerSet={enableRepsPerSet} enableRirPerSet={enableRirPerSet} />
										}}
									/>
								)}
							</AccordionDetails>
						</Accordion>
					</Grid>
				)
			})}
		</Grid>
	)
}

export default MasterWorkoutExerciseDaysList
